import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, of, catchError, ReplaySubject, tap } from 'rxjs';
import { User } from '../models/user.model';
import { environment } from '../../environments/environment';
import { UserApiResponse } from '../models-api/user-api.model';
import { mapUserFromApi, mapUserToApi } from '../models-mappers/user.mapper';

@Injectable({
  providedIn: 'root',
})
export class UserService {

  private apiUrl = `${environment.apiUrl}/users`;
  private http = inject(HttpClient);

  // Cache the user data
  private userSubject = new ReplaySubject<User>(1);
  public user$ = this.userSubject.asObservable();
  private isFetching = false;

  /**
   * Get the current user's profile
   * Uses cache if available, otherwise fetches from API
   */
  getCurrentUser(forceRefresh = false): Observable<User> {
    if (!forceRefresh && !this.isFetching) {
      // If we're not already fetching and we don't have a value yet (or we want to ensure we have one)
      // we'll trigger the fetch. ReplaySubject will handle subsequent subscribers.
      this.refreshUser().subscribe();
    }
    return this.user$;
  }

  /**
   * Refresh the user profile from the API
   */
  refreshUser(): Observable<User> {
    this.isFetching = true;
    return this.http.get<UserApiResponse>(`${this.apiUrl}/profile`).pipe(
      map(user => {
        const processedUser = mapUserFromApi(user);
        this.userSubject.next(processedUser);
        return processedUser;
      }),
      catchError(error => {
        console.warn('[UserService] Error fetching user profile:', error);
        this.userSubject.next(null as unknown as User);
        return of(null as unknown as User);
      }),
      tap(() => this.isFetching = false),
    );
  }

  /**
   * Update the current user's profile
   */
  updateProfile(userData: Partial<User>): Observable<User> {
    // Make a copy of the user data to avoid modifying the original
    const userToSend = { ...userData };

    // If the profileImage is a data URL, extract the base64 part
    if (userToSend.profileImage && userToSend.profileImage.startsWith('data:')) {
      const parts = userToSend.profileImage.split(',');
      if (parts.length === 2) {
        userToSend.profileImage = parts[1];
      }
    }

    return this.http.put<UserApiResponse>(`${this.apiUrl}/profile`, mapUserToApi(userToSend)).pipe(
      map(user => {
        const processedUser = mapUserFromApi(user);
        this.userSubject.next(processedUser);
        return processedUser;
      }),
      catchError(error => {
        console.error('Error updating user profile:', error);
        return of(null as unknown as User);
      }),
    );
  }

}
